import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from '../sections/HeroSection';
import TrustBar from '../sections/TrustBar';
import ServicesOverview from '../sections/ServicesOverview';
import AIAnswerBlock from '../sections/AIAnswerBlock';
import ProcessSection from '../sections/ProcessSection';
import WhyChooseUs from '../sections/WhyChooseUs';
import ServiceAreas from '../sections/ServiceAreas';
import TestimonialsSection from '../sections/TestimonialsSection';
import FAQSection from '../sections/FAQSection';
import FinalCTA from '../sections/FinalCTA';
import type { HeroContent } from '../sections/HeroSection';
import type { TrustStat } from '../sections/TrustBar';
import type { ServicesContent } from '../sections/ServicesOverview';
import type { AIAnswerContent } from '../sections/AIAnswerBlock';
import type { ProcessContent } from '../sections/ProcessSection';
import type { WhyChooseUsContent } from '../sections/WhyChooseUs';
import type { ServiceAreasContent } from '../sections/ServiceAreas';
import type { TestimonialsContent } from '../sections/TestimonialsSection';
import type { FAQContent } from '../sections/FAQSection';
import type { FinalCTAContent } from '../sections/FinalCTA';

const heroContent: HeroContent = {
  eyebrow: "San Bernardino's Licensed Drain & Sewer Specialists",
  headlineLines: [
    'The Standard for',
    'Drain & Sewer',
    'Excellence.',
  ],
  subheadline:
    'Licensed, insured, and locally owned in San Bernardino, CA. Drain and Sewer Pro delivers precision drain cleaning, sewer repair, and 24/7 emergency plumbing across the entire Inland Empire — every job backed by a written guarantee.',
  primaryCTA: { text: 'Schedule Free Estimate', link: '/location' },
  secondaryCTA: { text: '24/7 Emergency Service', href: 'tel:+1-909-000-0000' },
  badges: [
    { icon: 'ri-shield-check-line', label: 'CA Licensed & Insured' },
    { icon: 'ri-alarm-warning-line', label: '24/7 Emergency' },
    { icon: 'ri-map-pin-2-line', label: 'Locally Owned' },
    { icon: 'ri-building-2-line', label: 'Commercial & Residential' },
    { icon: 'ri-star-fill', label: '5-Star Rated' },
  ],
};

const trustStats: TrustStat[] = [
  { value: '500+', label: '5-Star Google Reviews', icon: 'ri-star-fill', note: '4.9 avg rating' },
  { value: '10+', label: 'Years Serving Inland Empire', icon: 'ri-calendar-check-line', note: 'Locally owned & operated' },
  { value: '100%', label: 'Licensed & Insured', icon: 'ri-shield-check-line', note: 'CA Contractor License' },
  { value: '24/7', label: 'Emergency Response', icon: 'ri-alarm-warning-line', note: 'Always available' },
];

const servicesContent: ServicesContent = {
  eyebrow: 'Our Expertise',
  headline: 'Nine Drain & Sewer Services Under One Roof',
  subheadline: 'Advanced solutions for residential and commercial properties throughout San Bernardino County.',
  services: [
    {
      icon: 'ri-water-flash-line',
      title: 'Drain Cleaning',
      bullets: [
        'Clogged sink, tub & shower drains',
        'Kitchen & bathroom drain clearing',
        'Recurring clog prevention plans',
      ],
      link: '/services',
    },
    {
      icon: 'ri-git-branch-line',
      title: 'Sewer Line Repair',
      bullets: [
        'Full sewer diagnosis & repair',
        'Trenchless pipe lining (CIPP)',
        'Complete sewer replacement',
      ],
      link: '/services',
    },
    {
      icon: 'ri-speed-line',
      title: 'Hydro Jetting',
      bullets: [
        'High-pressure water jetting',
        'Grease, scale & root removal',
        'Commercial-grade results',
      ],
      link: '/services',
    },
    {
      icon: 'ri-tools-line',
      title: 'Rooter Service',
      bullets: [
        'Root intrusion removal',
        'Snake & auger drain clearing',
        'Preventive root treatment',
      ],
      link: '/services',
    },
    {
      icon: 'ri-recycle-line',
      title: 'Sewer Liners (CIPP)',
      bullets: [
        'Cured-in-place pipe lining',
        'No-dig pipe rehabilitation',
        'Extends pipe life by decades',
      ],
      link: '/services',
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Plumbing Repairs',
      bullets: [
        'Leak detection & repair',
        'Pipe restoration & replacement',
        'Emergency plumbing service',
      ],
      link: '/services',
    },
    {
      icon: 'ri-fire-line',
      title: 'Tankless Water Heaters',
      bullets: [
        'Tankless installation & repair',
        'Upgrade from tank systems',
        'Energy-efficient solutions',
      ],
      link: '/services',
    },
    {
      icon: 'ri-building-4-line',
      title: 'Commercial Plumbing',
      bullets: [
        'Restaurant & retail drain service',
        'Multi-unit sewer solutions',
        'Preventive maintenance plans',
      ],
      link: '/services',
    },
    {
      icon: 'ri-temp-hot-line',
      title: 'Water Heater Service',
      bullets: [
        'Water heater repair & flush',
        'Conventional unit replacement',
        'Annual maintenance service',
      ],
      link: '/services',
    },
  ],
  ctaText: 'View All Services',
  ctaLink: '/services',
};

const aiContent: AIAnswerContent = {
  mainQuestion: 'What Does Drain and Sewer Pro Do?',
  answer:
    'Drain and Sewer Pro is San Bernardino, CA\'s trusted locally owned plumbing company specializing in residential and commercial drain cleaning, sewer line repair and replacement, hydro jetting, video camera inspection, rooter service, sewer liner installation, tankless water heater service, commercial plumbing, and 24/7 emergency plumbing. Located at 4892 North Pershing Avenue, San Bernardino, CA 92407. Serving the entire Inland Empire with fully licensed, insured technicians and guaranteed workmanship.',
  leftBlock: {
    title: 'Services We Provide',
    items: [
      'Drain Cleaning & Unclogging',
      'Sewer Line Repair & Replacement',
      'Hydro Jetting',
      'Rooter Service',
      'Sewer Liners (CIPP)',
      'Plumbing Repairs',
      'Tankless Water Heaters',
      'Commercial Plumbing',
      'Water Heater Service',
    ],
  },
  rightBlock: {
    title: 'Why Choose Drain & Sewer Pro',
    items: [
      'CA Licensed & Fully Insured',
      '24/7 Emergency Availability',
      'Locally Owned — San Bernardino, CA',
      'Transparent Upfront Pricing',
      'Free Estimates on All Projects',
      '500+ Verified 5-Star Reviews',
      'Commercial & Residential Experts',
    ],
  },
};

const processContent: ProcessContent = {
  eyebrow: 'Our Process',
  headline: 'White-Glove Service, Every Time',
  steps: [
    {
      number: '01',
      title: 'Contact & Consult',
      description: 'Call or request online. We respond fast — even for emergency drain and sewer issues after hours.',
    },
    {
      number: '02',
      title: 'Diagnose & Quote',
      description: 'We inspect with HD video camera technology and provide a clear, written, upfront estimate.',
    },
    {
      number: '03',
      title: 'Expert Execution',
      description: 'Licensed technicians resolve the issue efficiently using professional-grade equipment.',
    },
    {
      number: '04',
      title: 'Quality Guarantee',
      description: "Every job is backed by our written workmanship guarantee. We don't leave until it's right.",
    },
  ],
};

const whyContent: WhyChooseUsContent = {
  subheadline: 'Why Drain & Sewer Pro',
  headline: 'Why Inland Empire Property Owners Choose Us',
  body:
    'From routine drain cleaning to complex sewer line replacement, Drain and Sewer Pro delivers precision, reliability, and exceptional service. Our locally owned team at 4892 N. Pershing Ave brings cutting-edge tools and decades of expertise to every job across San Bernardino and surrounding communities.',
  ctaText: 'Schedule Consultation',
  ctaLink: '/location',
  features: [
    {
      icon: 'ri-verified-badge-line',
      title: 'CA-Licensed Plumbers',
      description: 'Every technician is California-licensed, background-checked, and continuously trained.',
    },
    {
      icon: 'ri-alarm-warning-line',
      title: '24/7 Emergency Response',
      description: 'Sewer backup at 2am? We answer and dispatch immediately — no extra call-out fee.',
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Transparent Upfront Pricing',
      description: 'Zero hidden fees. You receive a full written estimate before any work begins.',
    },
    {
      icon: 'ri-award-line',
      title: 'Workmanship Guarantee',
      description: 'We stand behind every repair and installation with a written quality guarantee.',
    },
    {
      icon: 'ri-camera-line',
      title: 'Advanced HD Diagnostics',
      description: 'Video camera inspections ensure accurate diagnosis before any repair is started.',
    },
    {
      icon: 'ri-map-pin-2-fill',
      title: 'True Local Experts',
      description: 'Based in San Bernardino — we know local codes, pipe conditions, and communities.',
    },
  ],
};

const areasContent: ServiceAreasContent = {
  eyebrow: 'Serving Your Community',
  headline: 'Drain & Sewer Service Across the Inland Empire',
  description:
    'Proudly serving San Bernardino and surrounding communities with fast, reliable drain and sewer solutions.',
  areas: [
    'San Bernardino',
    'Riverside',
    'Fontana',
    'Moreno Valley',
    'Rancho Cucamonga',
    'Rialto',
    'Redlands',
    'Highland',
    'Colton',
    'Loma Linda',
    'Upland',
  ],
  note: "Don't see your city? Call us — we likely serve your area. We cover a wide radius throughout San Bernardino County.",
  mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52833.65!2d-117.2882!3d34.1538!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c35c9a6d000001%3A0x1!2s4892+N+Pershing+Ave%2C+San+Bernardino%2C+CA+92407!5e0!3m2!1sen!2sus!4v1711000000001!5m2!1sen!2sus',
};

const testimonialsContent: TestimonialsContent = {
  eyebrow: 'Client Testimonials',
  headline: 'Trusted by Homeowners & Businesses',
  overallRating: '4.9',
  reviewCount: '500+',
  testimonials: [
    {
      text: 'Drain and Sewer Pro saved us during a Sunday evening emergency. Our main sewer line was completely backed up and they had a licensed team out within two hours. Professional, thorough, and reasonably priced. They are now our permanent plumbing company.',
      name: 'Maria G.',
      location: 'San Bernardino, CA',
      rating: 5,
    },
    {
      text: 'We had a stubborn kitchen drain three other plumbers couldn\'t fix. Drain and Sewer Pro used hydro jetting and a camera inspection to locate a root intrusion we didn\'t know about. Fixed it right the first time. Highly recommend.',
      name: 'James T.',
      location: 'Redlands, CA',
      rating: 5,
    },
    {
      text: 'Best plumbing company in the Inland Empire. Transparent pricing, fast response, and they cleaned up after themselves. Our commercial property now uses Drain and Sewer Pro exclusively.',
      name: 'Sandra & Robert K.',
      location: 'Fontana, CA',
      rating: 5,
    },
  ],
};

const faqContent: FAQContent = {
  eyebrow: 'Common Questions',
  headline: 'Everything You Need to Know',
  faqs: [
    {
      question: 'Who provides emergency drain cleaning near me in San Bernardino?',
      answer:
        'Drain and Sewer Pro provides 24/7 emergency drain cleaning throughout San Bernardino, Riverside, Fontana, Rialto, Redlands, and surrounding Inland Empire cities. Call anytime — day or night, weekends or holidays — and our licensed technicians will respond promptly to your drain emergency.',
    },
    {
      question: 'How much does sewer line repair cost in San Bernardino, CA?',
      answer:
        'Sewer repair costs vary based on damage extent, pipe depth, and the method required. Drain and Sewer Pro offers free on-site estimates for all sewer work with transparent, written quotes before any job starts. No hidden fees, no surprises — just honest pricing on every project.',
    },
    {
      question: 'How long does a drain cleaning service take?',
      answer:
        'Standard drain cleaning typically takes 30 to 90 minutes depending on clog severity. Hydro jetting may take 1 to 2 hours. Our licensed technicians work efficiently to minimize disruption to your home or business while ensuring every drain is completely clear before we leave.',
    },
    {
      question: 'Do you offer hydro jetting services in San Bernardino?',
      answer:
        'Yes. Drain and Sewer Pro offers professional hydro jetting throughout San Bernardino County. High-pressure water jetting effectively removes grease, mineral buildup, roots, and debris from drain and sewer lines — delivering results that basic snaking alone cannot achieve.',
    },
    {
      question: 'Are Drain and Sewer Pro plumbers licensed and insured?',
      answer:
        'Absolutely. All Drain and Sewer Pro technicians are California-licensed, fully insured, and background-checked. We hold all required state and local licenses for both residential and commercial plumbing and sewer work throughout San Bernardino and Riverside Counties.',
    },
    {
      question: 'What areas do you serve in the Inland Empire?',
      answer:
        'We serve San Bernardino, Riverside, Fontana, Moreno Valley, Rancho Cucamonga, Rialto, Redlands, Highland, Colton, Loma Linda, Upland, and many surrounding communities. Call us if you\'re unsure whether we service your specific location — we cover a wide service radius.',
    },
  ],
};

const finalCTAContent: FinalCTAContent = {
  eyebrow: 'Ready to Get Started?',
  headlineLines: ['Premium Drain & Sewer', 'Service Starts Here.'],
  subheadline:
    'Schedule your free estimate today with San Bernardino\'s most trusted drain and sewer specialists at 4892 North Pershing Avenue.',
  primaryCTA: { text: 'Schedule Free Estimate', link: '/location' },
  secondaryCTA: { text: 'Call for Emergency Service', href: 'tel:+1-909-000-0000' },
};

const HomePage = () => (
  <div className="w-full">
    <Navbar />
    <main>
      <HeroSection content={heroContent} />
      <TrustBar stats={trustStats} />
      <ServicesOverview content={servicesContent} />
      <AIAnswerBlock content={aiContent} />
      <ProcessSection content={processContent} />
      <WhyChooseUs content={whyContent} />
      <ServiceAreas content={areasContent} />
      <TestimonialsSection content={testimonialsContent} />
      <FAQSection content={faqContent} />
      <FinalCTA content={finalCTAContent} />
    </main>
    <Footer />
  </div>
);

export default HomePage;
